import React, { useRef, useState } from "react"
import { View, ScrollView, StyleSheet, Text, Linking, Platform } from "react-native"
import GradientButtonM from "@common/components/button/GradientButtonM"
import { theme } from "@common/theme/theme"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { STRIPE_PUBLISHABLE_KEY } from "@data/constants/apiConstants"

// Stripe Elements n'est disponible que sur web — les funnels d'acquisition
// (Brevet, Cours d'été) sont web-first (lead capture depuis fr.masteur.com).
// Sur native on dégrade proprement.
let stripePromise = null
let stripeLibs = null
if (Platform.OS === "web") {
    // require dynamique pour éviter de casser le bundle natif si la lib n'est pas dispo.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { loadStripe } = require("@stripe/stripe-js")
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const checkout = require("@stripe/react-stripe-js/checkout")
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY)
    stripeLibs = {
        CheckoutProvider: checkout.CheckoutProvider,
        PaymentElement: checkout.PaymentElement,
        // CheckoutProvider exposes BillingAddressElement / ShippingAddressElement,
        // not the legacy AddressElement (that one lives in the root @stripe/react-stripe-js).
        AddressElement: checkout.BillingAddressElement,
        useCheckout: checkout.useCheckout,
    }
}

const Guarantee = ({ text }) => (
    <View style={styles.guaranteeRow}>
        <FontAwesome5
            name="check-circle"
            solid
            size={12}
            color={theme.colors.success}
        />
        <Text style={styles.guaranteeText}>{text}</Text>
    </View>
)

const PaymentSecurityNotice = ({ width }) => (
    <View style={[styles.paymentSecurityNotice, { width }]}>
        <View style={styles.paymentSecurityContent}>
            <View style={styles.paymentSecurityCopy}>
                <View style={styles.paymentSecurityTitleRow}>
                    <FontAwesome5
                        name="shield-alt"
                        solid
                        size={11}
                        color={theme.colors.success}
                    />
                    <Text style={styles.paymentSecureText}>
                        Sécurisé par Stripe · validé par votre banque
                    </Text>
                </View>
                <Text style={styles.paymentSecurityDetail}>
                    Vos données bancaires jamais stockées par Masteur
                </Text>
            </View>
        </View>
    </View>
)

const CheckoutBody = ({
    summary,
    scrollRef,
    buttonLabel,
    guarantees,
    containerWidth,
    onSuccess,
    onError,
    creatingSession,
    sessionError,
    secureBadge,
    belowContent,
    /** Props posées sur le ScrollView (un gabarit parent qui suit le défilement). */
    scrollProps,
}) => {
    const { PaymentElement, useCheckout } = stripeLibs
    const checkoutState = useCheckout()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const stripeBlockY = useRef(0)

    const scrollToStripe = () => {
        scrollRef.current?.scrollTo({
            y: Math.max(0, stripeBlockY.current - 16),
            animated: true,
        })
    }

    const handlePay = async () => {
        setError(null)
        setLoading(true)
        try {
            const result = await checkoutState.checkout.confirm()
            if (result?.error) {
                setError(result.error.message || "Paiement refusé.")
                onError(result.error.code || result.error.type)
                setLoading(false)
                // Stripe Elements gates errors like incomplete card / missing field
                // here. Scroll the payment block back into view so the user sees
                // what's missing — the inline error renders right below it.
                scrollToStripe()
                return
            }
            const paymentIntentId =
                result?.paymentIntent?.id ||
                checkoutState?.session?.payment_intent ||
                null
            onSuccess(paymentIntentId)
        } catch (e) {
            setError("Une erreur inattendue est survenue.")
            onError("unexpected_exception")
            setLoading(false)
            scrollToStripe()
        }
    }

    return (
        <>
            <ScrollView
                ref={scrollRef}
                {...scrollProps}
                contentContainerStyle={styles.content}
                showsVerticalScrollIndicator={false}
            >
                {summary}

                <Text
                    style={[styles.sectionTitle, { marginTop: 22 }]}
                    onLayout={(e) =>
                        (stripeBlockY.current = e.nativeEvent.layout.y)
                    }
                >
                    Paiement
                </Text>

                <View style={styles.stripeBlock}>
                    <PaymentElement options={{ layout: "tabs" }} />
                </View>

                {secureBadge && (
                    <PaymentSecurityNotice width={containerWidth - 40} />
                )}

                {!secureBadge ? (
                    <View style={styles.secureNote}>
                        <FontAwesome5
                            name="lock"
                            size={11}
                            color={theme.colors.light}
                        />
                        <Text style={styles.secureText}>
                            Paiement sécurisé via Stripe.
                        </Text>
                    </View>
                ) : null}

                {guarantees?.length > 0 && (
                    <View style={styles.guaranteesBox}>
                        {guarantees.map((text, i) => (
                            <Guarantee key={i} text={text} />
                        ))}
                    </View>
                )}

                {(error || sessionError) && (
                    <View style={styles.errorBox}>
                        <Text style={styles.errorText}>
                            {error || sessionError}
                        </Text>
                    </View>
                )}

                {/* Contenu opt-in en bas de la zone de paiement (ex. FAQ). */}
                {belowContent}
            </ScrollView>
            <View style={styles.footer}>
                <GradientButtonM
                    label={buttonLabel}
                    onPress={handlePay}
                    loading={loading || creatingSession}
                    icon={
                        <FontAwesome5
                            name="lock"
                            size={16}
                            color={theme.colors.white}
                        />
                    }
                    style={{ width: containerWidth - 40 }}
                />
                <Text style={styles.cguText}>
                    En cliquant, j'accepte les{" "}
                    <Text
                        style={styles.cguLink}
                        onPress={() =>
                            Linking.openURL("https://masteur.com/cgu")
                        }
                    >
                        Conditions générales d'utilisation
                    </Text>
                </Text>
            </View>
        </>
    )
}

/**
 * Bloc de paiement embarqué partagé par les funnels d'acquisition (Brevet,
 * Cours d'été). Possède le chargement des libs Stripe (web-only), le
 * CheckoutProvider, le PaymentElement, les garanties, la note sécurisée, le
 * placeholder / sessionError quand le clientSecret n'est pas prêt, et le
 * fallback natif. Il rend UNIQUEMENT le contenu interne — l'écran appelant
 * garde son propre wrapper externe (root > frame > header) et passe un
 * containerWidth.
 */
const FunnelEmbeddedCheckout = ({
    clientSecret,
    summary,
    buttonLabel,
    guarantees = [],
    onSuccess,
    onError,
    creatingSession,
    sessionError,
    containerWidth,
    nativeNotice,
    scrollRef: externalScrollRef,
    secureBadge = false,
    belowContent = null,
    // Opt-in : contenu rendu AUSSI dans l'état sans clientSecret (préparation /
    // sessionError) — typiquement la FAQ + la bannière d'aide téléphone, pour
    // que le support reste joignable quand le paiement ne s'initialise pas.
    // Défaut null → comportement des funnels existants (brevet/été) inchangé.
    fallbackBelowContent = null,
    scrollProps,
}) => {
    const internalScrollRef = useRef(null)
    const scrollRef = externalScrollRef || internalScrollRef

    // Native fallback — Stripe Elements n'est pas dispo, on indique au lead
    // de finaliser sur web. (Le funnel est web-first ; on garde l'écran navigable.)
    if (Platform.OS !== "web" || !stripeLibs) {
        return (
            <ScrollView
                ref={scrollRef}
                {...scrollProps}
                contentContainerStyle={styles.content}
                showsVerticalScrollIndicator={false}
            >
                {summary}
                <View style={styles.nativeNotice}>
                    <Text style={styles.nativeNoticeText}>{nativeNotice}</Text>
                </View>
            </ScrollView>
        )
    }

    const { CheckoutProvider } = stripeLibs

    if (clientSecret) {
        return (
            <CheckoutProvider
                key={clientSecret}
                stripe={stripePromise}
                options={{
                    fetchClientSecret: async () => clientSecret,
                }}
            >
                <CheckoutBody
                    summary={summary}
                    scrollRef={scrollRef}
                    buttonLabel={buttonLabel}
                    guarantees={guarantees}
                    containerWidth={containerWidth}
                    scrollProps={scrollProps}
                    onSuccess={onSuccess}
                    onError={onError}
                    creatingSession={creatingSession}
                    sessionError={sessionError}
                    secureBadge={secureBadge}
                    belowContent={belowContent}
                />
            </CheckoutProvider>
        )
    }

    return (
        <ScrollView
            ref={scrollRef}
            {...scrollProps}
            contentContainerStyle={styles.content}
            showsVerticalScrollIndicator={false}
        >
            {summary}
            {sessionError ? (
                <View style={styles.errorBox}>
                    <Text style={styles.errorText}>{sessionError}</Text>
                </View>
            ) : (
                <View style={styles.preparingNote}>
                    <Text style={styles.preparingText}>
                        Préparation du paiement sécurisé…
                    </Text>
                </View>
            )}
            {fallbackBelowContent}
        </ScrollView>
    )
}

const styles = StyleSheet.create({
    content: {
        paddingTop: 14,
        paddingBottom: 24,
    },
    sectionTitle: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 18,
        color: theme.colors.primary,
        marginBottom: 10,
    },
    guaranteesBox: {
        backgroundColor: theme.colors.successLight,
        borderRadius: 10,
        padding: 12,
        marginTop: 18,
        gap: 8,
    },
    guaranteeRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
    },
    guaranteeText: {
        fontFamily: "Montserrat-Medium",
        fontSize: 12,
        color: theme.colors.successDark,
        flex: 1,
    },
    stripeBlock: {
        backgroundColor: theme.colors.white,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        borderRadius: 10,
        padding: 12,
    },
    secureNote: {
        flexDirection: "row",
        alignItems: "center",
        marginTop: 8,
        gap: 5,
    },
    secureText: {
        fontFamily: "Montserrat-Regular",
        fontSize: 11,
        color: theme.colors.light,
        flex: 1,
    },
    preparingNote: {
        marginTop: 16,
        padding: 12,
        backgroundColor: theme.colors.lighter,
        borderRadius: 8,
        alignItems: "center",
    },
    preparingText: {
        fontFamily: "Montserrat-Medium",
        fontSize: 12,
        color: theme.colors.grey,
    },
    nativeNotice: {
        marginTop: 16,
        padding: 12,
        backgroundColor: theme.colors.primaryLighter,
        borderRadius: 8,
    },
    nativeNoticeText: {
        fontFamily: "Montserrat-Medium",
        fontSize: 12,
        color: theme.colors.primary,
        lineHeight: 17,
    },
    errorBox: {
        marginTop: 12,
        backgroundColor: theme.colors.errorLight,
        padding: 10,
        borderRadius: 8,
    },
    errorText: {
        fontFamily: "Montserrat-Medium",
        fontSize: 12,
        color: theme.colors.error,
    },
    footer: {
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
        alignItems: "center",
        gap: 10,
    },
    paymentSecurityNotice: {
        borderRadius: 12,
        backgroundColor: theme.colors.primaryLighter,
        paddingVertical: 9,
        paddingHorizontal: 10,
        marginTop: 10,
    },
    paymentSecurityContent: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },
    paymentSecurityTitleRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
    },
    paymentSecurityCopy: {
        flexShrink: 1,
    },
    paymentSecureText: {
        fontFamily: "Montserrat-SemiBold",
        fontSize: 10,
        lineHeight: 13,
        color: theme.colors.primary,
        textAlign: "center",
    },
    paymentSecurityDetail: {
        fontFamily: "Montserrat-Regular",
        fontSize: 10,
        lineHeight: 13,
        color: theme.colors.grey,
        marginTop: 1,
    },
    cguText: {
        fontFamily: "Montserrat-Regular",
        fontSize: 11,
        color: theme.colors.grey,
        textAlign: "center",
        paddingHorizontal: 20,
    },
    cguLink: {
        color: theme.colors.primary,
        textDecorationLine: "underline",
        fontFamily: "Montserrat-Bold",
    },
})

export default FunnelEmbeddedCheckout
